/**
 * Hlavní layout aplikace po přihlášení.
 * Routing mezi záložkami + lazy načítání pohledů.
 */
import { renderPrehled } from './prehled.js';
import { renderHistorie } from './historie.js';
import { renderMista } from './mista.js';
import { renderAdministrace } from './administrace.js';
import { renderMzdy } from './mzdy.js';
import { renderFond } from './fond.js';
import { renderNastaveni } from './nastaveni.js';
import { renderGalerie } from './galerie.js';
import { renderRepertoir } from './repertoir.js';
import { renderTipy } from './tipy.js';
import { renderAnketa } from './anketa.js';
import { renderAktuality } from './aktuality.js';
import { renderGalerieWebu } from './galerie-webu.js';
import { renderHudbaVidea } from './hudba-videa.js';
import { renderWebObsah } from './web-obsah.js';
import { renderZkouska } from './zkouska.js';
import { openNewEventModal } from '../components/new-event-modal.js';
import { renderUcet } from './ucet.js';
import { supabase } from '../lib/supabase.js';

function esc(s) {
    if (s == null) return '';
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;')
        .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

const TABS = [
    { id: 'prehled',      label: 'Přehled',                  adminOnly: false },
    { id: 'historie',     label: 'Historie',                 adminOnly: false },
    { id: 'repertoir',    label: 'Repertoár',                adminOnly: false },
    { id: 'tipy',         label: 'Nové tipy',                adminOnly: false },
    { id: 'zkouska',      label: 'Zkouška',                  adminOnly: false },
    { id: 'anketa',       label: 'Anketa',                   adminOnly: false },
    { id: 'aktuality',    label: 'Aktuality',                adminOnly: false },
    { id: 'administrace', label: 'Administrace / Finance',   adminOnly: true  },
    { id: 'mzdy',         label: 'Mzdy / Výplaty',           adminOnly: true  },
    { id: 'galerie',      label: 'Galerie',                  adminOnly: false },
    { id: 'galerie-webu', label: 'Galerie webu',             adminOnly: false },
    { id: 'hudba-videa',  label: 'Hudba a videa',            adminOnly: false },
    { id: 'web-obsah',    label: 'Obsah webu',               adminOnly: true  },
    { id: 'mista',        label: 'Místa',                    adminOnly: false },
    { id: 'fond',         label: 'Fond',                     adminOnly: true  },
    { id: 'nastaveni',    label: 'Nastavení aplikace',       adminOnly: true  },
    { id: 'ucet',         label: 'Nastavení',                adminOnly: false }
];

const ACTIVE_TAB_STORAGE_KEY = 'kapela_active_tab';

export async function renderApp(root, { session, profile, onLogout }) {
    const isAdmin = profile?.role === 'admin';
    const visibleTabs = TABS.filter((t) => !t.adminOnly || isAdmin);

    // Obnov aktivní záložku z předchozího sezení (přežije refresh i přepínání oken)
    const savedTab = sessionStorage.getItem(ACTIVE_TAB_STORAGE_KEY);
    const activeTabId = visibleTabs.find((t) => t.id === savedTab)?.id || visibleTabs[0]?.id;

    // Načti název kapely z nastavení (default 'Kapela')
    const { data: settingsRows } = await supabase.from('settings').select('key, value').eq('key', 'band_name');
    const bandName = (settingsRows?.[0]?.value || 'Kapela').trim() || 'Kapela';

    const activeLabel = visibleTabs.find((t) => t.id === activeTabId)?.label || 'Menu';

    root.innerHTML = `
        <header>
            <button class="menu-toggle" id="menu-toggle" aria-label="Rozbalit menu" aria-expanded="false">
                <span class="menu-icon">☰</span>
                <span class="menu-current">${esc(activeLabel)}</span>
            </button>
            <div class="brand">
                <h1>${esc(bandName)}</h1>
                <span class="subtitle">sezóna ${new Date().getFullYear()}</span>
            </div>
            <div class="nav-actions">
                ${isAdmin ? '<button class="btn btn-sm btn-primary" id="new-event-btn">+ Nová akce</button>' : ''}
                <div class="user-badge">
                    <span class="name">${profile?.name || session.user.email}</span>
                    ${isAdmin ? '<span class="role">administrátor</span>' : ''}
                </div>
                <button class="btn btn-sm" id="logout-btn">Odhlásit</button>
            </div>
        </header>
        <nav class="tabs" id="tabs-nav">
            ${visibleTabs
                .map(
                    (t) =>
                        `<button class="tab ${t.id === activeTabId ? 'active' : ''}" data-view="${t.id}">${t.label}${t.adminOnly ? ' <span class="lock">admin</span>' : ''}</button>`
                )
                .join('')}
        </nav>
        <main id="view-container"></main>
    `;

    root.querySelector('#logout-btn').addEventListener('click', () => onLogout());

    const newBtn = root.querySelector('#new-event-btn');
    if (newBtn) newBtn.addEventListener('click', () => {
        openNewEventModal(() => {
            // Re-render aktuální záložky
            const active = root.querySelector('.tab.active');
            if (active) renderView(active.dataset.view);
        });
    });

    // Mobilní rozbalovací menu
    const tabsNav = root.querySelector('#tabs-nav');
    const menuToggle = root.querySelector('#menu-toggle');
    const closeMenu = () => {
        tabsNav.classList.remove('open');
        menuToggle.setAttribute('aria-expanded', 'false');
    };
    menuToggle.addEventListener('click', () => {
        const open = tabsNav.classList.toggle('open');
        menuToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    });

    root.querySelectorAll('.tab').forEach((tab) =>
        tab.addEventListener('click', () => {
            root.querySelectorAll('.tab').forEach((t) => t.classList.remove('active'));
            tab.classList.add('active');
            sessionStorage.setItem(ACTIVE_TAB_STORAGE_KEY, tab.dataset.view);
            // Aktualizuj popisek v tlačítku menu a zavři ho
            const lbl = root.querySelector('.menu-current');
            if (lbl) lbl.textContent = TABS.find((t) => t.id === tab.dataset.view)?.label || 'Menu';
            closeMenu();
            renderView(tab.dataset.view);
        })
    );

    // První pohled při startu — obnovený nebo default
    renderView(activeTabId);
}

function renderView(id) {
    // Aktivní pohled — využívá CSS (např. skrytí jména/odhlášení mimo Přehled).
    document.body.setAttribute('data-active-view', id);
    const container = document.getElementById('view-container');
    const tab = TABS.find((t) => t.id === id);

    switch (id) {
        case 'prehled':
            renderPrehled(container);
            break;
        case 'historie':
            renderHistorie(container);
            break;
        case 'mista':
            renderMista(container);
            break;
        case 'administrace':
            renderAdministrace(container);
            break;
        case 'mzdy':
            renderMzdy(container);
            break;
        case 'fond':
            renderFond(container);
            break;
        case 'nastaveni':
            renderNastaveni(container);
            break;
        case 'ucet':
            renderUcet(container);
            break;
        case 'galerie':
            renderGalerie(container);
            break;
        case 'galerie-webu':
            renderGalerieWebu(container);
            break;
        case 'hudba-videa':
            renderHudbaVidea(container);
            break;
        case 'web-obsah':
            renderWebObsah(container);
            break;
        case 'repertoir':
            renderRepertoir(container);
            break;
        case 'tipy':
            renderTipy(container);
            break;
        case 'anketa':
            renderAnketa(container);
            break;
        case 'aktuality':
            renderAktuality(container);
            break;
        case 'zkouska':
            renderZkouska(container);
            break;
        default:
            container.innerHTML = `
                <div class="placeholder">
                    <h2>${tab?.label || id}</h2>
                    <p>Tento pohled bude doplněn v dalším kroku (port z mockupu + Supabase).</p>
                </div>
            `;
    }
}
